import React from 'react';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from './common/LanguageSwitcher';
import './LandingPage.css';

const PartnersAgreementPage = () => {
  const navigate = useNavigate();

  return (
    <div className="landing-page">
      <nav className="landing-navbar">
        <div className="navbar-container">
          <div className="logo" onClick={() => navigate('/')}>
            <span className="material-icons">school</span>
            <span>Syllera</span>
          </div>
          <div className="nav-menu">
            <a href="/#features" onClick={(e) => { e.preventDefault(); navigate('/#features'); }}>Features</a>
            <a href="/#how-it-works" onClick={(e) => { e.preventDefault(); navigate('/#how-it-works'); }}>How It Works</a>
            <a href="/#pricing" onClick={(e) => { e.preventDefault(); navigate('/#pricing'); }}>Pricing</a>
            <a href="/#testimonials" onClick={(e) => { e.preventDefault(); navigate('/#testimonials'); }}>Reviews</a>
            <a href="/resources" onClick={(e) => { e.preventDefault(); navigate('/resources'); }}>Resources</a>
            <a href="/business" onClick={(e) => { e.preventDefault(); navigate('/business'); }}>Business</a>
            <a href="/partners" onClick={(e) => { e.preventDefault(); navigate('/partners'); }}>Partners</a>
          </div>
          <div className="nav-actions">
            <LanguageSwitcher className="landing-language-switcher" />
            <button className="btn-login" onClick={() => navigate('/#login')}>Login</button>
            <button className="btn-primary" onClick={() => navigate('/signup')}>Start Learning Free</button>
          </div>
        </div>
      </nav>

      <section className="section-container" style={{ paddingTop: '96px', paddingBottom: '72px' }}>
        <div className="section-content" style={{ maxWidth: 900 }}>
          <h1>Syllera Partner Agreement</h1>
          <p><strong>Last updated:</strong> 13-03-2026</p>
          <p>
            This Partner Agreement (&quot;Agreement&quot;) describes the terms governing participation
            in the Syllera Partner Program. By submitting a partner application and selecting
            &quot;I agree to the Partner Agreement&quot;, you confirm that you have read, understood,
            and accepted this Agreement.
          </p>
          <p>
            The Syllera Partner Program allows teachers, schools, and content creators to promote
            Syllera and earn commissions for referring students to the platform.
          </p>

          <h2>1. Eligibility</h2>
          <p>To participate in the Syllera Partner Program, applicants must:</p>
          <ul>
            <li>Be at least 18 years old</li>
            <li>Provide accurate and truthful information</li>
            <li>Have the ability to legitimately refer students or users to Syllera</li>
          </ul>
          <p>Applicants may apply as:</p>
          <ul>
            <li>Teachers</li>
            <li>Schools or educational institutions</li>
            <li>Influencers or content creators</li>
          </ul>
          <p>
            Schools may be required to provide proof of legal registration (such as Register de Commerce
            or an equivalent document).
          </p>
          <p>
            Syllera reserves the right to accept or reject any application at its sole discretion.
          </p>

          <h2>2. Partner Responsibilities</h2>
          <p>Partners agree to promote Syllera honestly, ethically, and in good faith.</p>
          <p>Partners must:</p>
          <ul>
            <li>Provide accurate information about Syllera</li>
            <li>Promote Syllera in a professional manner</li>
            <li>Avoid misleading claims about the platform or its services</li>
            <li>Follow all applicable laws and regulations</li>
          </ul>
          <p>
            Partners may not present themselves as employees, representatives, or official agents of Syllera.
          </p>

          <h2>3. Referral Tracking</h2>
          <p>
            Partners receive a unique referral link or referral code that allows Syllera to track student referrals.
          </p>
          <p>Referrals may be tracked using:</p>
          <ul>
            <li>Referral links</li>
            <li>Cookies</li>
            <li>Platform analytics</li>
          </ul>
          <p>Syllera cannot guarantee tracking in cases such as:</p>
          <ul>
            <li>Cookie deletion</li>
            <li>Browser restrictions</li>
            <li>Ad blockers</li>
            <li>Technical interruptions beyond Syllera&apos;s control</li>
          </ul>
          <p>Only valid referrals recorded by Syllera&apos;s systems are eligible for commissions.</p>

          <h2>4. Commissions and Payments</h2>
          <p>Partners may receive commissions for qualified referrals.</p>
          <p>A qualified referral typically means a student who:</p>
          <ul>
            <li>Registers on Syllera using a partner referral link or code</li>
            <li>Completes the required actions defined in the partner program (such as subscribing)</li>
          </ul>
          <p>Commission details, payment schedules, and payout conditions may be communicated:</p>
          <ul>
            <li>Inside the Partner Dashboard</li>
            <li>Via official Syllera communications</li>
          </ul>
          <p>Syllera reserves the right to:</p>
          <ul>
            <li>Modify commission structures</li>
            <li>Set minimum payout thresholds</li>
            <li>Adjust payment schedules with prior notice</li>
          </ul>

          <h2>5. Prohibited Activities</h2>
          <p>Partners may not engage in activities that harm Syllera or its users.</p>
          <p>Prohibited activities include:</p>
          <ul>
            <li>Sending spam emails or messages</li>
            <li>Creating fake student accounts</li>
            <li>Using misleading or deceptive advertising</li>
            <li>Impersonating Syllera</li>
            <li>Making false claims about the platform</li>
            <li>Attempting to manipulate referral tracking systems</li>
          </ul>
          <p>Any fraudulent activity may result in immediate termination from the partner program.</p>

          <h2>6. Intellectual Property</h2>
          <p>
            All trademarks, logos, branding, and content related to Syllera are the exclusive property of Syllera.
          </p>
          <p>Partners may not:</p>
          <ul>
            <li>Modify Syllera logos or branding</li>
            <li>Use Syllera branding in misleading ways</li>
            <li>
              Register domains, social media accounts, or advertisements that impersonate or closely mimic Syllera
            </li>
          </ul>
          <p>
            Partners may use official marketing materials provided by Syllera when promoting the platform.
          </p>

          <h2>7. Data Protection and Privacy</h2>
          <p>Partners must comply with all applicable data protection and privacy laws.</p>
          <p>Partners may not collect, store, or share student data without proper authorization.</p>
          <p>
            Student data and platform usage are governed by the{' '}
            <a href="/privacy-policy">Syllera Privacy Policy</a> and any applicable regulations.
          </p>

          <h2>8. Termination</h2>
          <p>Either party may terminate this partnership at any time.</p>
          <p>Syllera may terminate or suspend a partner account if:</p>
          <ul>
            <li>Fraud or abuse is detected</li>
            <li>Terms of this Agreement are violated</li>
            <li>Illegal or harmful promotion methods are used</li>
          </ul>
          <p>Upon termination:</p>
          <ul>
            <li>Referral links may be deactivated</li>
            <li>Pending commissions may be reviewed according to payout policies</li>
          </ul>

          <h2>9. Modification of Terms</h2>
          <p>Syllera may update this Agreement periodically.</p>
          <p>Updated versions will be published on the Syllera website.</p>
          <p>
            Continued participation in the partner program after updates constitutes acceptance of the revised terms.
          </p>

          <h2>10. Limitation of Liability</h2>
          <p>Syllera shall not be liable for:</p>
          <ul>
            <li>Indirect damages</li>
            <li>Lost profits</li>
            <li>Technical issues affecting referral tracking</li>
            <li>External platform failures beyond Syllera&apos;s control</li>
          </ul>
          <p>Participation in the partner program is at the partner&apos;s own risk.</p>

          <h2>11. Governing Law</h2>
          <p>
            This Agreement shall be governed by and interpreted in accordance with the laws of the applicable
            jurisdiction where Syllera operates.
          </p>

          <h2>12. Contact</h2>
          <p>
            For questions regarding the Syllera Partner Program, please contact:{' '}
            <a href="mailto:support@syllera.com">support@syllera.com</a>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default PartnersAgreementPage;

